/* eslint-disable no-console */
import 'cross-fetch/polyfill'
import defaults from 'lodash/defaults'
import each from 'lodash/each'
import get from 'lodash/get'
import isNil from 'lodash/isNil'
import isObjectLike from 'lodash/isObjectLike'

import { getToken, signOutLocal } from './auth/session'
import PublicGlobals from './PublicGlobals'
import { ApiMethod } from './types'

export class APIClientError extends Error {
  statusCode: number

  /**
   * Machine-readable error code, when the server sends one (e.g.
   * 'context_overflow' from the chat stream).
   */
  code?: string

  /**
   * Actionable, setting-level fixes the user can apply themselves — surfaced
   * next to the error message rather than discarded. The chat stream sends
   * these when a message is too large for the selected model, because "try
   * again" is useless advice for that failure: the retry fails identically
   * until a setting changes.
   */
  suggestions?: string[]

  /** Larger-context models worth switching to (admin-actionable). */
  largerModels?: string[]

  constructor(
    message: string,
    statusCode = 500,
    extra?: { code?: string; suggestions?: string[]; largerModels?: string[] }
  ) {
    super(message)
    this.statusCode = statusCode
    this.code = extra?.code
    this.suggestions = extra?.suggestions
    this.largerModels = extra?.largerModels
  }
}

export interface APIClientStreamOptions extends MakeRequestOptions {
  onProgress: (percentComplete: number) => void
}

export interface APIClientOptions extends MakeRequestOptions {
  isBlob?: boolean
}

export class APIClient {
  static createFullURLWithQueryParams(
    urlString: string,
    queryParams: { [paramName: string]: any },
    relative = false
  ): string {
    const url = new URL(urlString)

    each(queryParams, (paramValue, paramName) => {
      if (isNil(paramValue)) {
        return
      }

      if (isObjectLike(paramValue)) {
        paramValue = JSON.stringify(paramValue)
      } else {
        paramValue = paramValue.toString()
      }

      url.searchParams.set(paramName, paramValue)
    })

    return relative ? url.pathname + url.search : url.href
  }

  static async makeAPICall<T = any>(
    method: ApiMethod,
    resourcePath: string,
    options?: APIClientOptions
  ): Promise<T | any> {
    const response = await makeRequest(method, resourcePath, options)

    // makeRequest returns undefined when the request times out — surface a clear
    // error instead of crashing on `response.headers` of undefined.
    if (!response) {
      throw new Error('Request timed out')
    }

    // A 401 on an authenticated request means our session is no longer valid
    // (expired/revoked token): clear it and bounce to the login screen. Only do
    // this when we actually hold a token — otherwise an unauthenticated 401 (e.g.
    // the login request itself rejecting bad credentials) would wrongly reload
    // the page and swallow the error the caller needs to show.
    if (response.status === 401 && getToken()) {
      console.warn('Session is no longer valid. Signing out...')
      signOutLocal()
      return
    }

    const contentType = response.headers.get('Content-Type')
    if (contentType.indexOf('application/json') !== -1) {
      const responseData = await response.json()

      if (response.status > 299) {
        // Tolerate both the legacy APILove envelope ({ because: { message } }) and
        // aristotle-backend's exception shape ({ message }).
        let errorMessage =
          get(responseData, 'because.message') ||
          get(responseData, 'message') ||
          'There was an error. Try again.'
        if (response.status === 500) {
          errorMessage = 'Sorry, there was a problem. Please try again.'
        }

        throw new APIClientError(errorMessage, response.status)
      }

      // If the response is a 401 from the API, signout user
      if (responseData?.with?.statusCode === 401) {
        console.warn('User is not properly authenticated. Signing out...')
        signOutLocal()
        return
      }

      // Tolerate both the legacy APILove envelope ({ this, with: <data> }) and
      // aristotle-backend's raw DTO responses (the body IS the data).
      // NOTE: use hasOwnProperty + exclude arrays — `'with' in array` is TRUE
      // because Array.prototype.with (ES2023) exists, which would otherwise make
      // an array response resolve to that built-in function.
      const isEnvelope =
        responseData != null &&
        typeof responseData === 'object' &&
        !Array.isArray(responseData) &&
        Object.prototype.hasOwnProperty.call(responseData, 'with')
      return (isEnvelope ? responseData.with : responseData) as T
    }

    if (options.isBlob) {
      const blob = await response.blob()
      return blob
    }

    const bodyText = await response.text()
    if (response.status > 299) {
      throw new Error(bodyText)
    } else {
      return bodyText
    }
  }

  /**
   * Consume a Server-Sent Events (SSE) endpoint. Authenticates the same way as
   * makeAPICall (Bearer token + base URL) and parses the backend's frames:
   *   data: {"delta":"..."}   → onDelta(text)
   *   data: {"done":true}     → stream complete
   *   event: error\ndata:{..} → throws APIClientError
   */
  static async streamSSE(
    method: ApiMethod,
    resourcePath: string,
    options: MakeRequestOptions & {
      onDelta?: (text: string) => void
      onDone?: () => void
    } = {}
  ): Promise<void> {
    const token = getToken()
    if (!token && !options.noAuthorizationHeader) {
      throw new Error('No access token')
    }

    const headers = new Headers(options.headers)
    headers.set('Accept', 'text/event-stream')
    if (token && !options.noAuthorizationHeader) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    let body: BodyInit | undefined
    if (method !== 'GET' && options.body) {
      headers.set('Content-Type', 'application/json')
      body = JSON.stringify(options.body)
    }

    const baseURL = options.apiBaseURL || PublicGlobals.API_BASE_URL
    const response = await fetch(baseURL + resourcePath, {
      method,
      headers,
      body,
      credentials: 'include',
      ...(options.signal && { signal: options.signal }),
    })

    if (response.status === 401 && token) {
      signOutLocal()
      return
    }
    if (!response.ok || !response.body) {
      let message = 'There was a problem reaching the assistant.'
      let suggestions: string[] | undefined
      try {
        const errBody = await response.json()
        message = get(errBody, 'message') || message
        // Validation failures return a generic summary ("Invalid values: message")
        // plus an `errors` map holding the ACTUAL reason ("message must be shorter
        // than or equal to 4000 characters"). Surfacing only the summary tells the
        // user a field is wrong but never which rule it broke, which reads as an
        // unexplained failure. Prefer the specific messages.
        const fieldErrors = get(errBody, 'errors')
        if (fieldErrors && typeof fieldErrors === 'object') {
          const details = Object.values(fieldErrors as Record<string, unknown>)
            .flatMap((v) => (Array.isArray(v) ? v : [v]))
            .filter((v): v is string => typeof v === 'string' && v.trim().length > 0)
          if (details.length === 1) {
            message = details[0]
          } else if (details.length > 1) {
            suggestions = details
          }
        }
      } catch {
        /* non-JSON error body — keep the default message */
      }
      throw new APIClientError(message, response.status, { suggestions })
    }

    const reader = response.body.getReader()
    const decoder = new TextDecoder()
    let buffer = ''

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read()
      if (done) break
      buffer += decoder.decode(value, { stream: true })

      // SSE frames are separated by a blank line.
      let sep: number
      while ((sep = buffer.indexOf('\n\n')) !== -1) {
        const frame = buffer.slice(0, sep)
        buffer = buffer.slice(sep + 2)

        let eventName = 'message'
        const dataLines: string[] = []
        for (const line of frame.split('\n')) {
          if (line.startsWith('event:')) eventName = line.slice(6).trim()
          else if (line.startsWith('data:')) dataLines.push(line.slice(5).trim())
        }
        if (!dataLines.length) continue

        let payload: any
        try {
          payload = JSON.parse(dataLines.join('\n'))
        } catch {
          continue
        }

        if (eventName === 'error') {
          throw new APIClientError(payload?.message || 'Stream error', 500, {
            code: payload?.code,
            suggestions: Array.isArray(payload?.suggestions)
              ? payload.suggestions
              : undefined,
            largerModels: Array.isArray(payload?.largerModels)
              ? payload.largerModels
              : undefined,
          })
        }
        if (payload?.done) {
          options.onDone?.()
          return
        }
        if (typeof payload?.delta === 'string') {
          options.onDelta?.(payload.delta)
        }
      }
    }
    options.onDone?.()
  }
}

interface MakeRequestOptions {
  apiBaseURL?: string
  body?: any
  headers?: HeadersInit
  noAuthorizationHeader?: boolean
  queryParams?: object
  sendBodyAsJSON?: boolean
  signal?: AbortSignal
  timeoutInMS?: number
}

const makeRequest = async (
  method: ApiMethod,
  resourcePath: string,
  options: MakeRequestOptions
) => {
  options = defaults({}, options, {
    timeoutInMS: 10000,
    sendBodyAsJSON: true,
  })

  const request: RequestInit = {
    credentials: 'include',
    method,
    ...(options.signal && { signal: options.signal }),
  }
  const headers: HeadersInit = new Headers(options.headers)

  if (method !== 'GET' && options.body) {
    if (options.sendBodyAsJSON && isObjectLike(options.body)) {
      headers.set('Content-Type', 'application/json')
      request.body = JSON.stringify(options.body)
    } else {
      request.body = options.body
    }
  }

  const token = getToken()
  if (!token && !options.noAuthorizationHeader) {
    throw new Error('No access token')
  }

  if (token && !options.noAuthorizationHeader) {
    headers.set('Authorization', `Bearer ${token}`)
  }

  const baseURL = options.apiBaseURL || PublicGlobals.API_BASE_URL
  const url = APIClient.createFullURLWithQueryParams(baseURL + resourcePath, options.queryParams)

  let timedOut = false
  const timeout = setTimeout(() => {
    timedOut = true
  }, options.timeoutInMS)

  let response: Response

  try {
    request.headers = headers
    response = await fetch(url, request)
  } catch (e) {
    clearTimeout(timeout)
    if (timedOut) {
      throw new Error('Request timed out')
    }

    if (e.status === 401) {
      console.warn('User is not properly authenticated. Signing out...')
      console.error(e)

      signOutLocal()
    }

    throw e
  }

  clearTimeout(timeout)
  if (timedOut) {
    return
  }

  return response
}
